
// src/api/sitesApi.js
//
// Site / property management API client.
// All endpoints sit under /api/v1/sites and require an authenticated user
// with the appropriate sites.* permission. axiosClient.baseURL = "/api/v1",
// so paths here are relative.

import api from './axiosClient';

// ── Read ──────────────────────────────────────────────────────────────

/**
 * GET /api/v1/sites
 * Owner-scoped for non-SuperAdmin; SuperAdmin sees all sites.
 */
export async function listSites() {
  const { data } = await api.get('/sites');
  return data;
}

/**
 * GET /api/v1/sites/{id}
 * For non-SuperAdmin: "doesn't exist" and "not yours" both surface as
 * SITE_NOT_FOUND (intentional — prevents site-id enumeration).
 */
export async function getSite(id) {
  const { data } = await api.get(`/sites/${id}`);
  return data;
}

// ── Write ─────────────────────────────────────────────────────────────

/**
 * POST /api/v1/sites
 * Body: { name, domain }
 * Server normalizes domain (strips scheme/path/port, lowercases), issues
 * the initial public key automatically, signing is opt-in.
 */
export async function createSite({ name, domain }) {
  const { data } = await api.post('/sites', { name, domain });
  return data;
}

/**
 * PUT /api/v1/sites/{id}
 * Body: { name, domain, status }   status: "Active" | "Disabled"
 */
export async function updateSite(id, { name, domain, status }) {
  const { data } = await api.put(`/sites/${id}`, { name, domain, status });
  return data;
}

/**
 * DELETE /api/v1/sites/{id}
 * Cascades to all SiteKeys for this site.
 */
export async function deleteSite(id) {
  await api.delete(`/sites/${id}`);
}

// ── Keys ──────────────────────────────────────────────────────────────

/**
 * POST /api/v1/sites/{id}/keys/rotate
 * Revokes current active key, issues a fresh one.
 * Secret is NOT carried over — if signing was on, operator must
 * re-arm via generateSecret() against the new key.
 * Returns: { newKeyId, publicKey, revokedKeyId }
 */
export async function rotateKey(siteId) {
  const { data } = await api.post(`/sites/${siteId}/keys/rotate`);
  return data;
}

/**
 * POST /api/v1/sites/{id}/keys/secret
 * Generates (or regenerates) the signing secret on the currently active key.
 * Returns: { keyId, publicKey, secret, secretLastFour }
 *
 * ⚠️ The plaintext `secret` is delivered ONCE here and never again.
 * The UI MUST show it with copy-to-clipboard + a "won't see this again"
 * warning, then drop it from state.
 */
export async function generateSecret(siteId) {
  const { data } = await api.post(`/sites/${siteId}/keys/secret`);
  return data;
}

/**
 * DELETE /api/v1/sites/{id}/keys/secret
 * Clears the secret on the active key — signing disabled. Idempotent.
 */
export async function disableSecret(siteId) {
  await api.delete(`/sites/${siteId}/keys/secret`);
}